import Phaser from "phaser";
import { COLORS, TEXT_STYLES } from "../constants";

export type CombatTextKind = "damage" | "heal" | "shield";

export type CombatTextOptions = {
  kind: CombatTextKind;
  value: number;
  x: number;
  y: number;
  depth?: number;
  /** Optional prefix to show before the value (e.g., emoji icon) */
  prefix?: string;
};

const DEFAULT_DEPTH = 200;
const TEXT_STROKE = "#0f172a";

const getColorForKind = (kind: CombatTextKind): string => {
  switch (kind) {
    case "damage":
      return COLORS.damageText;
    case "heal":
      return COLORS.healText;
    case "shield":
      return COLORS.shieldText;
  }
};

export function spawnCombatText(
  scene: Phaser.Scene,
  opts: CombatTextOptions
): Phaser.GameObjects.Text {
  const sign = opts.kind === "damage" ? "-" : "+";
  const color = getColorForKind(opts.kind);
  const prefixStr = opts.prefix ? `${opts.prefix} ` : "";
  const baseText = `${prefixStr}${sign}${Math.abs(Math.floor(opts.value))}`;
  const jitterX = Phaser.Math.FloatBetween(-6, 6);
  const jitterY = Phaser.Math.FloatBetween(-4, 6);
  const startX = opts.x + jitterX;
  const startY = opts.y + jitterY;
  const upwardDrift =
    opts.kind === "damage" ? Phaser.Math.Between(24, 36) : Phaser.Math.Between(28, 40);
  const sideDrift = Phaser.Math.FloatBetween(-10, 10);
  const duration = opts.kind === "damage" ? 900 : 1100;
  const startScale = opts.kind === "damage" ? 0.5 : 0.6;
  const peakScale = opts.kind === "damage" ? 1.35 : 1.25;

  const text = scene.add
    .text(startX, startY, baseText, {
      ...TEXT_STYLES.combatText,
      color,
      stroke: TEXT_STROKE,
      strokeThickness: 5,
    })
    .setOrigin(0.5)
    .setDepth(opts.depth ?? DEFAULT_DEPTH);

  text.setScale(startScale);

  // Pop in quickly, then drift and fade
  scene.tweens.add({
    targets: text,
    scale: peakScale,
    duration: 120,
    ease: "Back.easeOut",
    onComplete: () => {
      scene.tweens.add({
        targets: text,
        x: startX + sideDrift,
        y: startY - upwardDrift,
        alpha: 0,
        scale: peakScale * 0.9,
        duration: duration - 120,
        ease: "Sine.easeOut",
        onComplete: () => text.destroy(),
      });
    },
  });

  return text;
}
